/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { Maybe } from '@amazon/vinyl-util'
import type { ImageFields } from '../gql/imageFieldsFrag'
import { maxBy, minBy } from './array'

export const ImageType = {
    BACKGROUND: 'BACKGROUND',
    PROFILE: 'PROFILE',
    // Landscape 16:9 podcast video artwork — must not go through
    // scaleToSquare (would letterbox the wide source into the square slot).
    VIDEO_THUMBNAIL: 'VIDEO_THUMBNAIL',
} as const

export function imagesOfType(
    images: Maybe<readonly Maybe<ImageFields>[]>,
    type: string
): readonly ImageFields[] {
    if (!images) return []
    return images.filter((image): image is ImageFields => {
        return image?.imageType === type
    })
}

export function getSmallestImage(
    images: Maybe<readonly Maybe<ImageFields>[]>,
    type?: string
): ImageFields | null {
    if (!images) return null
    if (type) images = imagesOfType(images, type)
    return minBy(images, (image) => image?.width ?? Number.MAX_VALUE) ?? null
}

export function getLargestImage(
    images: Maybe<readonly Maybe<ImageFields>[]>,
    type?: string
): ImageFields | null {
    if (!images) return null
    if (type) images = imagesOfType(images, type)
    return maxBy(images, (image) => image?.width ?? 0) ?? null
}

// Prefer the largest image tagged with `role`, else the largest of any role.
export function getPrimaryImage(
    images: Maybe<readonly Maybe<ImageFields>[]>,
    role: string = 'PRIMARY'
): ImageFields | null {
    return getLargestImage(images, role) ?? getLargestImage(images)
}

// Like getPrimaryImage, but a square source beats a wider non-square one.
export function getSquarePrimaryImage(
    images: Maybe<readonly Maybe<ImageFields>[]>,
    role: string
): ImageFields | null {
    const candidates = imagesOfType(images, role)
    const pool = candidates.length > 0 ? candidates : (images ?? [])
    const squares = pool.filter((image) => image?.width != null && image.width === image.height)
    return getLargestImage(squares.length > 0 ? squares : pool)
}
